// src/components/ReportModal.tsx
// ✅ ENHANCED: Support for user, group, and session reports
// 🛡️ PROTECTION: Automatic blocking for user reports

import {
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonButton,
  IonList,
  IonItem,
  IonLabel,
  IonRadioGroup,
  IonRadio,
  IonTextarea,
  IonText,
} from '@ionic/react';
import { useState } from 'react';
import {
  createUserReport,
  createGroupReport,
  createSessionReport,
  REPORT_REASONS,
  GROUP_REPORT_REASONS,
  SESSION_REPORT_REASONS,
  ReportReason,
  GroupReportReason,
  SessionReportReason,
} from '../services/reportService';

interface ReportModalProps {
  isOpen: boolean;
  onDismiss: () => void;
  reportType: 'user' | 'group' | 'session';
  reportedId: string;
  reportedName: string;
  onReportComplete?: () => void;
}

export const ReportModal: React.FC<ReportModalProps> = ({
  isOpen,
  onDismiss,
  reportType,
  reportedId,
  reportedName,
  onReportComplete,
}) => {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const getReasons = () => {
    switch (reportType) {
      case 'user':
        return REPORT_REASONS;
      case 'group':
        return GROUP_REPORT_REASONS;
      case 'session':
        return SESSION_REPORT_REASONS;
      default:
        return REPORT_REASONS;
    }
  };

  const getTitle = () => {
    switch (reportType) {
      case 'user':
        return 'Report User';
      case 'group':
        return 'Report Group';
      case 'session':
        return 'Report Session';
      default:
        return 'Report';
    }
  };

  const getMessage = () => {
    switch (reportType) {
      case 'user':
        return `You are reporting @${reportedName}. This user will be blocked and your report will be reviewed.`;
      case 'group':
        return `You are reporting the group "${reportedName}". Your report will be reviewed by moderators.`;
      case 'session':
        return `You are reporting the session "${reportedName}". Your report will be reviewed by moderators.`;
      default:
        return 'Your report will be reviewed.';
    }
  };

  const handleSubmit = async () => {
    if (!selectedReason) return;

    setSubmitting(true);

    let result;
    switch (reportType) {
      case 'user':
        result = await createUserReport(reportedId, selectedReason, description.trim() || undefined);
        break;
      case 'group':
        result = await createGroupReport(reportedId, selectedReason, description.trim() || undefined);
        break;
      case 'session':
        result = await createSessionReport(reportedId, selectedReason, description.trim() || undefined);
        break;
      default:
        result = { success: false, error: 'Invalid report type' };
    }

    setSubmitting(false);

    if (result.success) {
      setSelectedReason('');
      setDescription('');

      if (onReportComplete) {
        onReportComplete();
      }

      onDismiss();
    } else {
      alert(`Failed to submit report: ${result.error}`);
    }
  };

  const handleCancel = () => {
    setSelectedReason('');
    setDescription('');
    onDismiss();
  };

  return (
    <IonModal isOpen={isOpen} onDidDismiss={handleCancel}>
      <IonHeader>
        <IonToolbar>
          <IonTitle>{getTitle()}</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleCancel}>Cancel</IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent className="ion-padding">
        <IonText color="medium">
          <p style={{ marginBottom: '20px' }}>
            {getMessage()}
          </p>
        </IonText>

        <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>Reason for Report</h3>
        <IonList>
          <IonRadioGroup
            value={selectedReason}
            onIonChange={(e) => setSelectedReason(e.detail.value)}
          >
            {getReasons().map((reason) => (
              <IonItem key={reason}>
                <IonLabel>{reason}</IonLabel>
                <IonRadio slot="start" value={reason} />
              </IonItem>
            ))}
          </IonRadioGroup>
        </IonList>

        <div style={{ marginTop: '24px' }}>
          <h3 style={{ marginBottom: '12px', fontSize: '16px' }}>
            Additional Details (Optional)
          </h3>
          <IonTextarea
            value={description}
            onIonInput={(e) => setDescription(e.detail.value || '')}
            placeholder="Please provide any additional information that may help us review this report..."
            rows={4}
            maxlength={500}
            autocapitalize="sentences"
            style={{
              border: '1px solid var(--ion-color-medium)',
              borderRadius: '8px',
              padding: '8px',
            }}
          />
        </div>

        <IonButton
          expand="block"
          color="danger"
          onClick={handleSubmit}
          disabled={!selectedReason || submitting}
          style={{ marginTop: '24px' }}
        >
          {submitting ? 'Submitting...' : 'Submit Report'}
        </IonButton>

        <IonText color="medium">
          <p style={{ fontSize: '12px', marginTop: '16px', textAlign: 'center' }}>
            False reports may result in account suspension.
          </p>
        </IonText>
      </IonContent>
    </IonModal>
  );
};